import { ColumnTypes } from '../../../src/components/table/columnTypes';
import { createSelector } from '@opengov/compose-app/reselect';
import isBlank from '@sedan-utils/is-blank';
import { ColDef } from 'ag-grid-community';
import React from 'react';
import { selectDimensionalSummaryAgGridProps } from '../../dimensionalTable/selectors/selectDimensionalSummaryTableAgGrid';
import { selectAgGridColumnTypes } from '../ReportDataTableAgGrid/selectors/selectAgGridColumnTypes';
import { selectReportDataTableColumnDefsWithGroupByColumn } from '../ReportDataTableContainer/selectors/selectReportDataTableColumnDefs';
import { selectSummaryAgGridProps } from '../ReportDataTableAgGrid/selectors/selectReportSummaryTableAgGrid';
import { selectSummaryGridColumnDefs } from '../ReportDataTableAgGrid/selectors/selectSummaryGridColumnDefs';
import { selectPdfExportData, selectReportConfigWithMetadata } from '../selectors/selectReportExplorerState';
import { IPrintableRow, IPrintableTableProps, IPrintableTableColumn } from './PrintableTable';

export type UnderlineRole = 'subtotal' | 'sectionTotal' | 'statementTotal' | null;

export const getColumnStyles = (col?: ColDef): React.CSSProperties => {
  const colStyle: React.CSSProperties = { textAlign: 'left' };
  const columnType = col?.type;
  if (columnType === ColumnTypes.Number || columnType === ColumnTypes.Currency) {
    colStyle.textAlign = 'right';
  }
  return colStyle;
};

export const appendWbrTagToUnderscores = (str: string): string | JSX.Element => {
  let first = true;
  if (isBlank(str)) {
    return '';
  }
  return str.split('_').reduce(
    (acc, part) => {
      if (first) {
        first = false;
        return <>{part}</>;
      }
      return (
        <>
          {acc}
          _<wbr />
          {part}
        </>
      );
    },
    <></>
  );
};

export const selectPrintableDetailTableProps = createSelector(
  selectReportConfigWithMetadata,
  selectPdfExportData,
  selectAgGridColumnTypes,
  selectReportDataTableColumnDefsWithGroupByColumn,
  (reportConfig, data, formatter, columnDefs): IPrintableTableProps => {
    const groupByColumn = reportConfig?.detailTableQuery.groupByColumns[0];
    const keys = columnDefs.filter((column) => !column.hide).map((column) => column.field);
    let tableData = data.detailTableData;

    if (data.detailTableTotalsData?.length) {
      tableData = tableData.concat(data.detailTableTotalsData);
    }

    const formattedRows = tableData.map((row, index) => {
      const out: IPrintableRow = {
        data: {},
        style: {}
      };

      keys.forEach((property) => {
        const column = columnDefs.find((col) => col.field === property);
        out.data[property] = formatter[column?.type ?? '']?.valueFormatter({ value: row[property] });

        if (Object.keys(row).includes(groupByColumn) && row[groupByColumn]) {
          out.style[property] = { fontWeight: 'bold' };
        }

        // make the last row bold if totals are available
        if (data.detailTableTotalsData?.length && index === tableData.length - 1) {
          out.style[property] = { fontWeight: 'bold' };
        }
      });
      return out;
    });

    return {
      columns: columnDefs.reduce((acc, col) => {
        if (!col.hide) {
          acc.push({
            identifier: col.field,
            label: appendWbrTagToUnderscores(col.headerName),
            style: getColumnStyles(col)
          });
        }
        return acc;
      }, []),
      rows: formattedRows
    };
  }
);

type IColumnSubset = {
  field: string;
  type: string;
  headerName: string;
};

export function deriveUnderlineRole(groupingDepth: number, isTotalRow: boolean): UnderlineRole {
  if (groupingDepth === 0) return 'sectionTotal';
  if (isTotalRow) return 'subtotal';
  return null;
}

function promoteLastSectionTotal(rows: IPrintableRow[]): void {
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i].underlineRole === 'sectionTotal') {
      rows[i].underlineRole = 'statementTotal';
      return;
    }
  }
}

// this assumes that the row data is in a group then child ordering
export const selectPrintableSummaryTableProps = createSelector(
  selectSummaryAgGridProps,
  selectAgGridColumnTypes,
  selectSummaryGridColumnDefs,
  selectDimensionalSummaryAgGridProps,
  selectPdfExportData,
  (dataProps, formatter, columnSummaryDefs, dimensionalContainerProps, groupAndRowData): IPrintableTableProps => {
    let headerName = dataProps.gridOptions?.autoGroupColumnDef?.headerName ?? 'None';
    let data = dataProps.rowData;
    let columnDefs: IColumnSubset[] = columnSummaryDefs;
    // Account Summary reports turn nulls into 0 where the other Summary keeps them as blanks
    let emptyCellsAreZeroed = false;

    if (dimensionalContainerProps?.columnDefs?.length) {
      headerName = dimensionalContainerProps.gridOptions.autoGroupColumnDef?.headerName ?? headerName;
      data = groupAndRowData?.accountSummaryTableData;
      columnDefs = dimensionalContainerProps.columnDefs as unknown as IColumnSubset[];
      emptyCellsAreZeroed = true;
    }

    if (!data || data.length === 0) {
      return {
        columns: [],
        rows: []
      };
    }

    const keys = columnDefs.map((column) => column.field).concat('name');

    const formatted = data.map((row) => {
      const printableRow: IPrintableRow = {
        data: {},
        style: {}
      };

      const groupingDepth = row['name'].length - 1;

      if (groupingDepth > 0) {
        printableRow.style['name'] = { paddingLeft: `${groupingDepth * 2}em` };
      } else {
        printableRow.style['name'] = { fontWeight: 'bold' };
      }

      printableRow.underlineRole = deriveUnderlineRole(groupingDepth, row['isTotalRow'] === true);

      keys.forEach((property) => {
        // this is the left column ( grouping name )
        if (property === 'name') {
          // the last in the list is the name
          const name = row[property][groupingDepth];
          printableRow.data['name'] = name;
        } else {
          const type = columnDefs.find((col) => {
            return col.field === property;
          })?.type;
          let value = row[property];

          if (emptyCellsAreZeroed && (value === undefined || value === null)) {
            value = 0;
          }

          if (value === undefined || value === null) {
            printableRow.data[property] = '';
          } else {
            printableRow.data[property] = formatter[type].valueFormatter({ value });
          }

          if (groupingDepth === 0) {
            printableRow.style[property] = { fontWeight: 'bold' };
          }
        }
      });

      return printableRow;
    });

    promoteLastSectionTotal(formatted);

    const firstColumn: IPrintableTableColumn = {
      identifier: 'name',
      label: headerName,
      style: getColumnStyles()
    };

    const restOfColumns = columnDefs.map((col) => ({
      identifier: col.field,
      label: appendWbrTagToUnderscores(col.headerName),
      style: getColumnStyles(col)
    })) as IPrintableTableColumn[];

    const columns = [firstColumn].concat(restOfColumns);

    return {
      columns,
      rows: formatted
    };
  }
);
